import React, { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ProgressBar from "../../questionnaire/components/ProgressBar";
import AppButton from "../../questionnaire/components/AppButton";
import { useAppointmentSteps, useAppointmentStepPath } from "../../questionnaire/routes/stepConfig";
import { useWorkflow } from "../../questionnaire/contexts/WorkflowContext";
import { Alert } from "../../components/shared/Alert";
import axiosInstance from "../../utils/axiosConfig";

interface ScreenerSubmitPayload {
  questionnaireId: number;
  templateId: number;
  questions: { questionid: number; answer: string }[];
  score: string;
}

interface ScreenerQuestion {
  questionid: number;
  question: string;
  possibleanswers: string[];
  key: string;
}

interface ScreenerSection {
  headertext: string;
  questionlist: ScreenerQuestion[];
}

interface ScreenerQuestionnaire {
  questionnaireid: number;
  name: string;
  sections: ScreenerSection[];
}

interface ScreenerResponse {
  questionnairescreeners: ScreenerQuestionnaire[];
}

const WellnessCheck: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { getNextStepInSequence, getPreviousStep, isAllStepsCompleted, error: workflowError } = useWorkflow();

  const steps = useAppointmentSteps("wellness-check");
  const medicalPath = useAppointmentStepPath("medical-history");
  const reviewPath = useAppointmentStepPath("review-and-sign");

  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [screenerData, setScreenerData] = useState<ScreenerResponse | null>(null);
  const [allScreenersSubmitted, setAllScreenersSubmitted] = useState(false);

  React.useEffect(() => {
    const fetchScreenerQuestions = async () => {
      if (!appointmentId) return;

      setLoading(true);
      setLocalError(null);
      try {
        const response = await axiosInstance.get(`/api/athena/digital-checkin/appointment/${encodeURIComponent(String(appointmentId))}/screener-questions`, {
          headers: { client: 'axis', domain: 'axis' }
        });

        // Check if response is an empty array (all screeners already submitted)
        if (Array.isArray(response.data) && response.data.length === 0) {
          setAllScreenersSubmitted(true);
          setScreenerData(null);
        } else {
          // Normalize the response - handle both array and object formats
          const data = Array.isArray(response.data) 
            ? { questionnairescreeners: response.data }
            : (response.data as ScreenerResponse);
          setScreenerData(data);
          setAllScreenersSubmitted(false);
        }
      } catch (err) {
        setLocalError("Unable to load wellness questions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchScreenerQuestions();
  }, [appointmentId]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    // Collect all answers from all questionnaires
    const allAnswers: Record<number, ScreenerSubmitPayload> = {};

    screenerData?.questionnairescreeners.forEach((questionnaire) => {
      const answers: { questionid: number; answer: string }[] = [];

      questionnaire.sections.forEach((section) => {
        section.questionlist.forEach((q, qIndex) => {
          const answer = formData.get(`${questionnaire.questionnaireid}_${qIndex}`) as string;
          if (answer) {
            answers.push({
              questionid: q.questionid,
              answer,
            });
          }
        });
      });

      if (answers.length > 0) {
        // Extract templateid from the questionnaire; if not directly available, use questionnaireid as fallback
        const templateId = (questionnaire as any).templateid || questionnaire.questionnaireid;

        allAnswers[questionnaire.questionnaireid] = {
          questionnaireId: questionnaire.questionnaireid,
          templateId: templateId,
          questions: answers,
          score: String(answers.length * 2),
        };
      }
    });

    // Check if there are any answers to submit
    const hasAnswersToSubmit = Object.keys(allAnswers).length > 0;
    
    // If no answers to submit (user hasn't made any changes), just navigate without calling API
    if (!hasAnswersToSubmit) {
      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          // No next step in workflow (e.g. appointment only has screeners) -> go to complete
          navigate(`/appointments/${appointmentId}/complete`);
        }
      }
      return;
    }

    // Submit all questionnaires
    setSubmitting(true);
    setLocalError(null);
    try {
      for (const questionnaireId in allAnswers) {
        const qa = allAnswers[questionnaireId];
        try {
          await axiosInstance.put(`/api/athena/digital-checkin/appointment/${encodeURIComponent(String(appointmentId))}/screener-questions`, qa, {
            headers: { client: 'axis', domain: 'axis' }
          });
        } catch (error: any) {
          const errorText = typeof error.response?.data === 'string' 
            ? error.response.data 
            : JSON.stringify(error.response?.data || {});
          console.error("Submission error:", errorText);
          throw new Error(errorText || `HTTP ${error.response?.status || 'Unknown'}`);
        }
      }

      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          // No next step in workflow (e.g. appointment only has screeners) -> go to complete
          navigate(`/appointments/${appointmentId}/complete`);
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unable to save your information. Please try again.";
      setLocalError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndExit = async () => {
    setSubmitting(true);
    setLocalError(null);
    try {
      await axiosInstance.put(`/api/athena/digital-checkin/appointment/${encodeURIComponent(String(appointmentId))}/screener-questions`, { questionnaireId: 1, templateId: 1, questions: [], score: "0" }, {
        headers: { client: 'axis', domain: 'axis' }
      });

      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(reviewPath.replace("review-and-sign", "payments"));
        }
      }
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const progressSteps = steps;

  return (
    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      <div className="flex flex-row px-10 py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-2xl">
          Axis Integrated Mental Health
        </span>
        <span className="text-[#01545D] font-medium text-xl">Patient Check-In</span>
      </div>
      <main className="flex-grow container mx-auto flex flex-col items-center px-4 py-10">
        <h1 className="text-4xl font-semibold text-[#01545D] text-center mb-3">Wellness Check</h1>
        <div className="text-xl text-[#1C1C1C] text-center mb-6">
          Help us understand how you've been feeling
        </div>
        {loading && (
          <div className="text-sm text-[#5D6A85] mb-2">Loading wellness questions…</div>
        )}
        {(localError || workflowError) && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert 
              type="error" 
              onClose={() => {
                setLocalError(null);
              }}
            >
              {workflowError || localError}
            </Alert>
          </div>
        )}
        <ProgressBar steps={progressSteps} />
        {allScreenersSubmitted ? (
          <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4">
            <div className="flex items-start gap-3 bg-[#E0F2F3] border border-[#01545D] rounded-lg px-4 py-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#01545D] text-white text-xs font-bold mt-1">
                ✓
              </div>
              <div className="flex flex-col gap-1 text-sm text-[#1C1C1C]">
                <div className="font-semibold text-[#01545D]">All Screeners Already Submitted</div>
                <div>
                  All wellness check screeners have already been completed and submitted. You can proceed to the next step.
                </div>
              </div>
            </div>
            <div className="flex gap-5 w-full mt-8">
              <AppButton
                variant="secondary"
                type="button"
                onClick={() => {
                  const previousStep = getPreviousStep(location.pathname);
                  if (previousStep) {
                    navigate(previousStep.stepUrl);
                  } else {
                    navigate(medicalPath);
                  }
                }}
                disabled={!!workflowError || submitting}
                className="flex-1"
              >
                Back
              </AppButton>
              <AppButton
                type="button"
                onClick={() => {
                  if (isAllStepsCompleted()) {
                    navigate(`/appointments/${appointmentId}/complete`);
                  } else {
                    const nextStep = getNextStepInSequence(location.pathname);
                    if (nextStep) {
                      navigate(nextStep.stepUrl);
                    } else {
                      // No next step in workflow (e.g. appointment only has screeners) -> go to complete
                      navigate(`/appointments/${appointmentId}/complete`);
                    }
                  }
                }}
                disabled={!!workflowError || submitting}
                className="flex-1"
              >
                Continue
              </AppButton>
            </div>
          </div>
        ) : (
          <form
            className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4"
            onSubmit={handleSubmit}
          >
            {screenerData?.questionnairescreeners?.map((questionnaire) => (
            <div key={questionnaire.questionnaireid} className="flex flex-col gap-8">
              <div className="pb-4 border-b border-[#DCE1E8]">
                <h2 className="text-2xl font-semibold text-[#01545D]">{questionnaire.name}</h2>
              </div>
              {questionnaire.sections.map((section, sectionIndex) => (
                <div key={`section-${sectionIndex}-${section.headertext || 'no-header'}`} className="flex flex-col gap-6">
                  {section.headertext && (
                    <div className="text-lg font-medium text-[#1C1C1C]">{section.headertext}</div>
                  )}
                  <div className="flex flex-col gap-4">
                    {section.questionlist.map((q, qIndex) => (
                      <section key={q.questionid} className="flex flex-col gap-3">
                        <div className="text-base font-semibold text-[#1C1C1C]">
                          {q.question} <span className="text-red-500">*</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          {q.possibleanswers.map((opt) => (
                            <label
                              key={opt}
                              className={
                                "flex flex-col items-center justify-center border rounded-lg py-3 px-4 text-center cursor-pointer " +
                                (selectedAnswers[`${questionnaire.questionnaireid}_${qIndex}`] === opt
                                  ? "border-[#01545D] bg-[#E0F2F3]"
                                  : "border-[#DCE1E8] hover:border-[#01545D]")
                              }
                            >
                              <input
                                type="radio"
                                name={`${questionnaire.questionnaireid}_${qIndex}`}
                                value={opt}
                                className="hidden"
                                required
                                checked={selectedAnswers[`${questionnaire.questionnaireid}_${qIndex}`] === opt}
                                onChange={() => setSelectedAnswers((prev) => ({ ...prev, [`${questionnaire.questionnaireid}_${qIndex}`]: opt }))}
                              />
                              <span className="text-sm text-[#1C1C1C]">{opt}</span>
                            </label>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div className="flex items-start gap-3 bg-[#F7F8FD] border border-[rgba(1,84,93,0.3)] rounded-lg px-4 py-3 mt-2">
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#01545D] text-white text-xs font-bold mt-1">
              i
            </div>
            <div className="flex flex-col gap-1 text-sm text-[#1C1C1C]">
              <div className="font-semibold text-[#01545D]">Your Privacy Matters</div>
              <div>
                These questions help your provider assess your mental well-being. Your responses are confidential
                and protected by HIPAA.
              </div>
            </div>
          </div>

          <div className="flex gap-5 w-full mt-8">
            <AppButton
              variant="secondary"
              type="button"
              onClick={() => {
                const previousStep = getPreviousStep(location.pathname);
                if (previousStep) {
                  navigate(previousStep.stepUrl);
                } else {
                  navigate(medicalPath);
                }
              }}
              disabled={!!workflowError || submitting}
              className="flex-1"
            >
              Back
            </AppButton>
            {/* <AppButton
              variant="secondary"
              type="button"
              onClick={handleSaveAndExit}
              disabled={submitting}
              className="flex-1"
            >
              {submitting ? "Saving..." : "Save & Exit"}
            </AppButton> */}
            <AppButton type="submit" disabled={!!workflowError || submitting} className="flex-1">
              {submitting ? "Saving..." : "Continue"}
            </AppButton>
          </div>
        </form>
        )}
      </main>
    </div>
  );
};

export default WellnessCheck;